import { Link } from "react-router-dom";
import {
  TrendingUp,
  ArrowRight,
  Quote,
  CheckCircle2,
  Sparkles,
  ArrowUpRight,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";
import { RESULTS_CASES, RESULTS_STATS } from "@/lib/data";

const formatEUR = (n) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n || 0);

const Resultats = () => {
  return (
    <div data-testid="page-resultats">
      <SEO
        title="Résultats clients, Revenus multipliés en Bretagne"
        description="Découvrez les résultats concrets obtenus par nos propriétaires : revenus multipliés, occupation en hausse, tranquillité retrouvée. Cas réels à Saint-Malo, Dinard, Cancale, Dinan, Saint-Cast."
        keywords="résultats conciergerie Airbnb Bretagne, cas clients conciergerie, revenus Airbnb Saint-Malo, rentabilité location Dinard, performance Airbnb Cancale"
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Résultats clients", path: "/resultats" },
        ]}
      />
      <PageHero
        overline="Cas concrets · Chiffres réels"
        title="Des résultats qui"
        italic="parlent d'eux-mêmes"
        description="Chaque propriétaire est unique, chaque bien aussi. Voici ce que nous avons accompli pour nos clients, chiffres avant / après à l'appui, sans filtre."
        image="/images/resultats-hero.jpg"
        cta={{ to: "/contact", label: "Demander une étude" }}
      />

      {/* Stats bar */}
      <section className="py-16 lg:py-20 bg-cream border-y border-ink/5" data-testid="results-stats">
        <div className="container-tm">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
            {RESULTS_STATS.map((s) => (
              <div key={s.label} className="text-center lg:text-left">
                <p className="font-serif font-light text-gold text-5xl md:text-6xl leading-none tabular-nums">
                  {s.value}
                </p>
                <p className="mt-3 text-[11px] uppercase tracking-[0.22em] font-semibold text-ink/60">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case studies */}
      <section className="py-24 lg:py-32 bg-white" data-testid="results-cases">
        <div className="container-tm">
          <div className="max-w-2xl mb-16">
            <span className="overline inline-flex items-center gap-2">
              <TrendingUp size={12} strokeWidth={2} className="text-gold" />
              5 cas réels
            </span>
            <h2 className="mt-5 font-serif font-light text-ink text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Leurs biens
              <span className="italic text-gold"> avant</span> & <span className="italic text-gold">après</span>.
            </h2>
          </div>

          <div className="space-y-20 lg:space-y-28">
            {RESULTS_CASES.map((c, idx) => (
              <CaseCard key={c.id} caseData={c} reverse={idx % 2 === 1} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-gradient-to-br from-cream via-sand to-[#f0e6d0] text-ink relative overflow-hidden" data-testid="results-cta">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-gold/25 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] bg-sage/10 rounded-full blur-[100px] pointer-events-none" />
        <div className="container-tm relative text-center max-w-3xl mx-auto">
          <Sparkles size={22} strokeWidth={1.5} className="text-gold mx-auto mb-6" />
          <h3 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink">
            Votre bien peut faire
            <span className="italic text-gold"> bien mieux</span>.
          </h3>
          <p className="mt-6 font-sans text-base md:text-lg text-ink/70 leading-relaxed">
            Chaque logement a un potentiel unique. Nous l'identifions gratuitement.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact" className="btn-accent group" data-testid="results-cta-study">
              Demander une étude gratuite
              <ArrowRight size={14} strokeWidth={2} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/contact" state={{ scrollTo: "simulator" }} className="inline-flex items-center justify-center border border-ink/25 text-ink hover:bg-ink hover:text-white transition-all duration-300 px-7 py-3.5 font-sans uppercase tracking-[0.16em] text-xs font-semibold rounded-full" data-testid="results-cta-sim">
              Simuler mes revenus
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// ===== CaseCard =====
const CaseCard = ({ caseData: c, reverse }) => {
  return (
    <article
      className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
        reverse ? "lg:[&>*:first-child]:order-2" : ""
      }`}
      data-testid={`case-${c.id}`}
    >
      {/* Visual side */}
      <div className="lg:col-span-6">
        <div className="relative rounded-3xl overflow-hidden shadow-lift group">
          <img
            src={c.image}
            alt={`${c.title} - ${c.city}`}
            loading="lazy"
            className="w-full aspect-[4/3] object-cover group-hover:scale-[1.02] transition-transform duration-1000"
          />
          <div className="absolute top-4 left-4 px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full">
            <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-ink">
              {c.city}
            </span>
          </div>
          {/* Delta badge */}
          <div className="absolute bottom-4 right-4 bg-gold text-white rounded-2xl px-5 py-3 shadow-glow-amber">
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-white/85">
              Revenus
            </p>
            <p className="font-serif text-2xl leading-none mt-1 tabular-nums">
              +{c.delta}%
            </p>
          </div>
        </div>
      </div>

      {/* Content side */}
      <div className="lg:col-span-6">
        <span className="overline text-gold">{c.type}</span>
        <h3 className="mt-3 font-serif text-3xl md:text-4xl text-ink leading-tight">
          {c.title}
        </h3>

        {/* Before / After comparison */}
        <div className="mt-8 grid grid-cols-2 gap-4" data-testid={`comparison-${c.id}`}>
          <div className="rounded-2xl border border-ink/10 bg-ink/3 p-5">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-ink/50 mb-4">
              Avant
            </p>
            <StatLine label="Prix / nuit" value={formatEUR(c.before.nightly)} muted />
            <StatLine label="Nuits / an" value={c.before.occupancy} muted />
            <StatLine label="Revenu / an" value={formatEUR(c.before.revenueYear)} muted strong />
          </div>
          <div className="rounded-2xl border-2 border-gold bg-gold/5 p-5 relative">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-gold mb-4 flex items-center gap-1.5">
              <ArrowUpRight size={11} strokeWidth={2.4} />
              Après
            </p>
            <StatLine label="Prix / nuit" value={formatEUR(c.after.nightly)} />
            <StatLine label="Nuits / an" value={c.after.occupancy} />
            <StatLine label="Revenu / an" value={formatEUR(c.after.revenueYear)} strong highlight />
          </div>
        </div>

        {/* Services applied */}
        <div className="mt-7">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-sage mb-3">
            Actions menées · en {c.duration}
          </p>
          <ul className="grid sm:grid-cols-2 gap-2">
            {c.services.map((s) => (
              <li key={s} className="flex items-start gap-2 text-sm font-sans text-ink/80">
                <CheckCircle2 size={14} strokeWidth={2} className="text-gold shrink-0 mt-0.5" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Quote */}
        <figure className="mt-7 border-l-2 border-gold pl-5">
          <Quote size={16} strokeWidth={1.5} className="text-gold/50 mb-2" />
          <blockquote className="font-serif italic text-ink/85 text-lg leading-relaxed">
            « {c.quote} »
          </blockquote>
          <figcaption className="mt-3 text-xs text-ink/55 font-sans">
           , {c.author}
          </figcaption>
        </figure>
      </div>
    </article>
  );
};

const StatLine = ({ label, value, muted, strong, highlight }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className={`text-xs font-sans ${muted ? "text-ink/55" : "text-ink/65"}`}>
      {label}
    </span>
    <span
      className={`font-serif tabular-nums ${
        strong ? "text-lg md:text-xl" : "text-sm md:text-base"
      } ${highlight ? "text-gold font-medium" : muted ? "text-ink/70" : "text-ink"}`}
    >
      {value}
    </span>
  </div>
);

export default Resultats;
